import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { v4 as uuidv4 } from 'uuid';
import { UATConstants } from 'src/Common/Models/UATConstants';
import type {
  CreatedRecord,
  FieldOptionsResponse,
  FormView,
  LookupSearchResponse,
} from './formEngineTypes';

/**
 * RTK Query slice for the Common.FormEngine v1 endpoints that back the server-driven RoB create form.
 * All compute (cascades / options / AOG autofill / validation) is owned by the engine — the client
 * only renders whatever `FormView` the engine publishes and posts field values back.
 *
 * These are the NEW engine endpoints, so the slice targets `UATConstants.FormEngineApiUrl` (the local
 * Common.API dev server by default) rather than `CommonServicesWebApiUrl` used by every other,
 * unchanged API. Point them all at the same host per environment via `VITE_FORMENGINEAPIURL`.
 *
 * Endpoints (base = `UATConstants.FormEngineApiUrl`):
 *   - GET  /api/v1/forms/{recordType}?tenantId=        → blank FormView (defaults applied)
 *   - POST /api/v1/forms/{recordType}/view?tenantId=   → computed FormView; body is `{ Data, ChangedFields }`
 *   - POST /api/v1/request/{recordType}?tenantId=      → CreatedRecord; body is the BARE field bag
 *
 * The auth header logic mirrors `commonServicesBaseApi.ts` (Bearer jwtTokenForApi + Coreapitoken +
 * Graphtoken from sessionStorage); see that file for the canonical pattern.
 */

const prepareHeaders = (headers: Headers): Headers => {
  const token = sessionStorage.getItem(UATConstants.jwtTokenForApi);
  const coreAPIToken = sessionStorage.getItem(UATConstants.jwtTokenForCoreApi);
  const graphToken = sessionStorage.getItem(UATConstants.jwtTokenForGraph);

  if (token && coreAPIToken && graphToken) {
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('Coreapitoken', coreAPIToken);
    headers.set('Graphtoken', graphToken);
  }

  headers.set('Accept', 'application/json');

  return headers;
};

const NON_AI_RECORD_TYPE = 'NonAiCapacityRequest';

/**
 * Non-AI create accepts multipart data so attachment bytes are not serialized as empty JSON objects.
 * Other tenants keep the existing bare JSON field bag.
 */
export const buildCreateBody = (
  recordType: string,
  data: Record<string, unknown>
): Record<string, unknown> | FormData => {
  if (recordType !== NON_AI_RECORD_TYPE) {
    return data;
  }

  const { attachments, ...payload } = data;
  const formData = new FormData();
  formData.append(
    'data',
    new Blob([JSON.stringify(payload)], { type: 'application/json' })
  );

  if (Array.isArray(attachments)) {
    attachments.forEach((attachment) => {
      if (attachment instanceof File) {
        formData.append('attachments', attachment, attachment.name);
      }
    });
  }

  return formData;
};

const withTenant = (path: string, tenantId: string, subtype?: string): string => {
  const params = new URLSearchParams();
  if (tenantId) {
    params.set('tenantId', tenantId);
  }
  if (subtype) {
    params.set('subtype', subtype);
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

export interface RecordTypeArg {
  recordType: string;
  tenantId: string;
  /** Optional form subtype (e.g. AOAI `3PGPU` / `Non3PGPU`); appended as `?subtype=` when present. */
  subtype?: string;
}

export interface ComputeViewArg extends RecordTypeArg {
  data: Record<string, unknown>;
  /**
   * The union of field names the user changed since the previous recompute. The engine consumes this
   * set to drive autofill / cascade-clear and to PIN user-authored fields so they are never clobbered
   * (the Part C.4 change-driven invalidation + pinned-set model). Programmatic autofill writes must
   * NOT appear here — only genuine user edits. Omitted → treated as an empty set by the engine.
   */
  changedFields?: string[];
}

export interface CreateForTypeArg extends RecordTypeArg {
  data: Record<string, unknown>;
}

/** Args for the manage (edit) view: load an existing record as an edit-mode `FormView`. */
export interface ManageViewArg extends RecordTypeArg {
  /** The existing record id being edited (e.g. the RAID id). */
  recordId: string;
}

/** A `FormView` plus the record's current concurrency token (read from the GET `ETag` response header). */
export type ManageView = FormView & {
  /** The stored record's ETag; echo it as `If-Match` on the update. `null` when the server omitted it. */
  etag?: string | null;
};

/** Args for a partial update (PATCH) of an existing record, guarded by the caller's `If-Match` etag. */
export interface UpdateForTypeArg extends RecordTypeArg {
  /** The existing record id being updated. */
  recordId: string;
  /** The partial field bag (only the fields the user changed). */
  data: Record<string, unknown>;
  /** The `If-Match` concurrency token from the manage view GET; omit to opt out of conflict detection. */
  ifMatch?: string;
}

/** Args for resolving a single field's option list (Taxonomy-Central-backed for Marketplace geo/segment). */
export interface FieldOptionsArg {
  /** The engine record type / form id, e.g. `MarketplaceRequest`. */
  recordType: string;
  /** The engine field name (camelCase), e.g. `impactedGeography` — NOT the lowercase RAID key. */
  field: string;
  /** Optional free-text filter (server-side contains). */
  q?: string;
  /** Optional cap on the number of options returned. */
  take?: number;
}

/** Args for `GET /api/v1/lookups/{entityType}`. */
export interface EntityLookupArg {
  entityType: string;
  q: string;
  take?: number;
}

export const formEngineApi = createApi({
  reducerPath: 'formEngineApi',
  baseQuery: fetchBaseQuery({
    baseUrl: UATConstants.FormEngineApiUrl,
    prepareHeaders,
  }),
  endpoints: (builder) => ({
    /** Blank view for the initial render. */
    getBlankView: builder.query<FormView, RecordTypeArg>({
      query: ({ recordType, tenantId, subtype }) =>
        withTenant(`/api/v1/forms/${encodeURIComponent(recordType)}`, tenantId, subtype),
    }),

    /**
     * Recompute the view for a partial field bag. The body wraps the bag in a `Data` envelope and
     * carries `ChangedFields` — the union of fields the user changed this debounce window — so the
     * engine can pin user edits and drive cascade-clear / autofill (Part C.4).
     */
    computeView: builder.mutation<FormView, ComputeViewArg>({
      query: ({ recordType, tenantId, subtype, data, changedFields }) => ({
        url: withTenant(`/api/v1/forms/${encodeURIComponent(recordType)}/view`, tenantId, subtype),
        method: 'POST',
        body: { Data: data, ChangedFields: changedFields ?? [] },
      }),
    }),

    /** One-shot create. NOTE: the body is the BARE field bag (no envelope). */
    createForType: builder.mutation<CreatedRecord, CreateForTypeArg>({
      query: ({ recordType, tenantId, subtype, data }) => ({
        url: withTenant(`/api/v1/request/${encodeURIComponent(recordType)}`, tenantId, subtype),
        method: 'POST',
        body: buildCreateBody(recordType, data),
        headers: { 'Idempotency-Key': uuidv4() },
      }),
    }),

    /**
     * Manage (edit) view: load an existing record as an EDIT-MODE `FormView` (populated values, manage
     * definition + rules, incremental-save validation) to seed the server-driven manage form. The record's
     * current concurrency token is read from the `ETag` response header and returned as `etag`, so the client
     * can send it back as `If-Match` on the update.
     */
    getManageView: builder.query<ManageView, ManageViewArg>({
      query: ({ recordType, tenantId, subtype, recordId }) =>
        withTenant(
          `/api/v1/forms/${encodeURIComponent(recordType)}/${encodeURIComponent(recordId)}`,
          tenantId,
          subtype
        ),
      transformResponse: (response: FormView, meta): ManageView => ({
        ...response,
        etag: meta?.response?.headers.get('ETag') ?? null,
      }),
    }),

    /**
     * Partial update (manage save). Sends only the changed fields to `PATCH /api/v1/request/{recordType}/{id}`
     * with the `If-Match` etag from the manage view; the engine hydrates the stored record, validates the edit
     * as an incremental save, and persists under Cosmos optimistic concurrency (a stale etag → 412). The new
     * ETag rides the response header for the next edit.
     */
    updateForType: builder.mutation<CreatedRecord, UpdateForTypeArg>({
      query: ({ recordType, tenantId, subtype, recordId, data, ifMatch }) => ({
        url: withTenant(
          `/api/v1/request/${encodeURIComponent(recordType)}/${encodeURIComponent(recordId)}`,
          tenantId,
          subtype
        ),
        method: 'PATCH',
        body: data,
        headers: ifMatch ? { 'If-Match': ifMatch } : undefined,
      }),
    }),

    /**
     * Resolves the option list for a single field, live from its registered option source. Used by the
     * Marketplace MANAGE form to pull Impacted Region / Segment from Taxonomy Central (CORP_GEO /
     * SEGMENTATION) — the same source the server-driven create form uses.
     */
    getFieldOptions: builder.query<FieldOptionsResponse, FieldOptionsArg>({
      query: ({ recordType, field, q, take }) => {
        const params = new URLSearchParams();
        if (q) {
          params.set('q', q);
        }
        if (typeof take === 'number') {
          params.set('take', String(take));
        }
        const qs = params.toString();
        return `/api/v1/forms/${encodeURIComponent(recordType)}/fields/${encodeURIComponent(field)}/options${qs ? `?${qs}` : ''}`;
      },
    }),

    /** Searches a server-owned entity source for an entity-picker typeahead. */
    searchEntities: builder.query<LookupSearchResponse, EntityLookupArg>({
      query: ({ entityType, q, take }) => {
        const params = new URLSearchParams({ q });
        if (typeof take === 'number') {
          params.set('take', String(take));
        }
        return `/api/v1/lookups/${encodeURIComponent(entityType)}?${params.toString()}`;
      },
    }),
  }),
});

export const {
  useGetBlankViewQuery,
  useComputeViewMutation,
  useCreateForTypeMutation,
  useGetManageViewQuery,
  useUpdateForTypeMutation,
  useGetFieldOptionsQuery,
  useLazySearchEntitiesQuery,
} = formEngineApi;
// Generated by GitHub Copilot | Created on: 2026-06-24 | New file | RTK Query slice for Common.FormEngine v1 (blank view / compute view / create) backing the server-driven RoB form
// Generated by GitHub Copilot | Updated on: 2026-07-08 | 20 lines modified | Added changedFields to ComputeViewArg + POST /view body { Data, ChangedFields } for the pinned-set model
// Generated by GitHub Copilot | Updated on: 2026-07-08 | 2 lines modified | Base URL now UATConstants.FormEngineApiUrl (localhost dev) so the new FormEngine APIs target the local server
// Generated by GitHub Copilot | Updated on: 2026-07-21 | 45 lines added | Added getManageView (GET forms/{recordType}/{id} + ETag capture) + updateForType (PATCH request/{recordType}/{id} If-Match) for the Marketplace manage form
// Generated by GitHub Copilot | Updated on: 2026-08-03 | 15 lines removed | Removed AOAI-only routeCapacityIntake endpoint + hook + CapacityIntake* imports (non-marketplace tenant, stashed to _stashed-serverdriven-aoai-rob)
// Generated by GitHub Copilot | Updated on: 2026-09-26 | 22 lines added | Added generic entity lookup query
// Generated by GitHub Copilot | Updated on: 2026-10-22 | Copilot: Reconcile proven source/GHE deltas; preserve both histories.
